/**
 * Statistiques descriptives sur les nuits.
 *
 * Module **pur** : ni React ni Dexie.
 *
 * ## Deux pieges evites ici
 *
 * 1. **Un jour sans nuit n'est pas une nuit de zero heure.** C'est une absence
 *    de mesure. La confondre avec un zero ferait plonger toutes les moyennes
 *    des qu'une automatisation rate un declenchement — c'est-a-dire souvent.
 *    Les series portent donc `null`, et les moyennes ignorent ces jours.
 *
 * 2. **Une heure de coucher ne se moyenne pas directement.** 23 h 50 et
 *    00 h 10 ont pour moyenne arithmetique 12 h 00, soit midi. Tous les
 *    calculs d'horaire passent par `minutesCentrees`, qui rapporte les heures
 *    du soir a un axe continu autour de minuit.
 */

import {
  JOUR_MS,
  MINUTE_MS,
  epoch,
  minutesCentrees,
  minutesCentreesVersHoraire,
  shiftDayKey,
  todayKey,
} from '../domain/time'
import type { DayKey, Nuit } from '../domain/types'
import { mesurerNuit, type MesuresNuit } from './nuits'

export interface PointJour {
  date: DayKey
  nuit: Nuit | null
  mesures: MesuresNuit | null
}

/**
 * Serie dense sur les `jours` derniers jours : un point par jour, meme sans
 * nuit. La densite est ce qui rend les trous visibles au lieu de les masquer
 * en resserrant les barres.
 */
export function serie(nuits: Nuit[], jours: number, aujourdhui: DayKey = todayKey()): PointJour[] {
  const parDate = new Map(nuits.map((n) => [n.date, n]))
  const points: PointJour[] = []
  for (let k = jours - 1; k >= 0; k--) {
    const date = shiftDayKey(aujourdhui, -k)
    const nuit = parDate.get(date) ?? null
    points.push({ date, nuit, mesures: nuit ? mesurerNuit(nuit) : null })
  }
  return points
}

/** Les `jours` derniers jours, nuits presentes uniquement. */
export function fenetre(nuits: Nuit[], jours: number, aujourdhui: DayKey = todayKey()): Nuit[] {
  const debut = shiftDayKey(aujourdhui, -(jours - 1))
  return nuits.filter((n) => n.date >= debut && n.date <= aujourdhui)
}

// ---------------------------------------------------------------------------
// Outils
// ---------------------------------------------------------------------------

export function moyenne(valeurs: number[]): number | null {
  if (valeurs.length === 0) return null
  return valeurs.reduce((a, b) => a + b, 0) / valeurs.length
}

export function mediane(valeurs: number[]): number | null {
  if (valeurs.length === 0) return null
  const t = [...valeurs].sort((a, b) => a - b)
  const m = Math.floor(t.length / 2)
  if (t.length % 2 === 1) return t[m] as number
  return (((t[m - 1] as number) + (t[m] as number)) / 2)
}

/** Ecart-type de population : on decrit la serie observee, on n'infere pas. */
export function ecartType(valeurs: number[]): number | null {
  const m = moyenne(valeurs)
  if (m === null || valeurs.length < 2) return null
  const v = valeurs.reduce((s, x) => s + (x - m) ** 2, 0) / valeurs.length
  return Math.sqrt(v)
}

/** Milieu de nuit, sur l'axe continu centre sur minuit. */
export function milieuDeNuit(nuit: Nuit): number {
  const milieu = (epoch(nuit.debut) + epoch(nuit.fin)) / 2
  return minutesCentrees(new Date(milieu).toISOString())
}

// ---------------------------------------------------------------------------
// Resume
// ---------------------------------------------------------------------------

export interface Resume {
  nombre: number
  moyenneMs: number | null
  medianeMs: number | null
  efficaciteMoyenne: number | null
  reveilsMoyens: number | null
  /** Regularite : ecart-type des horaires, en minutes. Plus c'est bas, plus
   *  les nuits se ressemblent — la mesure la plus actionnable du lot. */
  regulariteCoucherMin: number | null
  regulariteLeverMin: number | null
  coucherMoyen: string | null
  leverMoyen: string | null
  /** Cumul de `objectif − temps pose` sur la fenetre. Positif = dette. */
  detteMs: number
}

export function resumer(nuits: Nuit[], objectifMinutes: number): Resume {
  const mesures = nuits.map(mesurerNuit)
  const durees = mesures.map((m) => m.tempsPoseMs)
  const couchers = nuits.map((n) => minutesCentrees(n.debut))
  const levers = nuits.map((n) => minutesCentrees(n.fin))

  const objectifMs = objectifMinutes * 60_000
  const dette = durees.reduce((s, d) => s + (objectifMs - d), 0)

  const mCoucher = moyenne(couchers)
  const mLever = moyenne(levers)

  return {
    nombre: nuits.length,
    moyenneMs: moyenne(durees),
    medianeMs: mediane(durees),
    efficaciteMoyenne: moyenne(mesures.map((m) => m.efficacite)),
    reveilsMoyens: moyenne(mesures.map((m) => m.reveils)),
    regulariteCoucherMin: ecartType(couchers),
    regulariteLeverMin: ecartType(levers),
    coucherMoyen: mCoucher === null ? null : minutesCentreesVersHoraire(mCoucher),
    leverMoyen: mLever === null ? null : minutesCentreesVersHoraire(mLever),
    detteMs: dette,
  }
}

// ---------------------------------------------------------------------------
// Par jour de semaine
// ---------------------------------------------------------------------------

const JOURS_FR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']

export interface LigneJourSemaine {
  jour: string
  indice: number
  nombre: number
  moyenneMs: number | null
}

function jourDeLaSemaine(date: DayKey): number {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).getDay()
}

/** Regroupe par jour de **lever**, du lundi au dimanche. */
export function parJourSemaine(nuits: Nuit[]): LigneJourSemaine[] {
  const seaux = new Map<number, number[]>()
  for (const n of nuits) {
    const j = jourDeLaSemaine(n.date)
    const liste = seaux.get(j)
    const duree = mesurerNuit(n).tempsPoseMs
    if (liste) liste.push(duree)
    else seaux.set(j, [duree])
  }

  // Ordre francais : lundi d'abord, dimanche en dernier.
  const ordre = [1, 2, 3, 4, 5, 6, 0]
  return ordre.map((indice) => {
    const valeurs = seaux.get(indice) ?? []
    return {
      jour: JOURS_FR[indice] as string,
      indice,
      nombre: valeurs.length,
      moyenneMs: moyenne(valeurs),
    }
  })
}

/**
 * Decalage social : ecart entre le milieu de nuit des jours de repos et celui
 * des jours travailles.
 *
 * Se coucher et se lever deux heures plus tard le week-end revient a changer
 * de fuseau horaire chaque vendredi. La valeur est en millisecondes, et `null`
 * tant qu'il manque des nuits d'un cote ou de l'autre — une moyenne sur une
 * seule nuit de week-end ne dirait rien.
 */
export function decalageSocial(nuits: Nuit[]): number | null {
  const repos: number[] = []
  const semaine: number[] = []
  for (const n of nuits) {
    const j = jourDeLaSemaine(n.date)
    // Lever un samedi ou un dimanche : la nuit precedente etait libre.
    if (j === 0 || j === 6) repos.push(milieuDeNuit(n))
    else semaine.push(milieuDeNuit(n))
  }
  if (repos.length < 2 || semaine.length < 2) return null
  const mr = moyenne(repos)
  const ms = moyenne(semaine)
  if (mr === null || ms === null) return null
  return Math.abs(mr - ms) * MINUTE_MS
}

// ---------------------------------------------------------------------------
// Frise
// ---------------------------------------------------------------------------

/** Position d'un instant sur un axe horaire allant de 18 h a 14 h le lendemain. */
export const FRISE_DEBUT_H = 18
export const FRISE_DUREE_H = 20

export interface BarreFrise {
  date: DayKey
  /** Fractions 0..1 sur l'axe de la frise. */
  debut: number
  fin: number
  /** Interruptions decomptees, en fractions 0..1. */
  trous: { debut: number; fin: number }[]
}

/**
 * Convertit les nuits en barres positionnees sur un axe 18 h -> 14 h.
 *
 * Les valeurs hors axe sont bornees plutot qu'omises : une nuit commencee a
 * 17 h reste une nuit, elle est simplement collee au bord gauche.
 */
export function frise(points: PointJour[]): (BarreFrise | null)[] {
  const largeurMs = FRISE_DUREE_H * 3_600_000

  // L'ancre etant deja 18 h, la position est simplement l'ecart a l'ancre
  // rapporte a la largeur de l'axe.
  const position = (instant: string, ancre: number): number =>
    Math.max(0, Math.min(1, (epoch(instant) - ancre) / largeurMs))

  return points.map((p) => {
    if (!p.nuit) return null
    const n = p.nuit
    // Ancre : 18 h locales du soir precedant le lever.
    const jourLever = new Date(epoch(n.fin))
    jourLever.setHours(FRISE_DEBUT_H, 0, 0, 0)
    let ancre = jourLever.getTime()
    if (epoch(n.debut) < ancre) ancre -= JOUR_MS

    return {
      date: p.date,
      debut: position(n.debut, ancre),
      fin: position(n.fin, ancre),
      trous: n.interruptions
        .filter((i) => !i.ignoree && epoch(i.fin) > epoch(i.debut))
        .map((i) => ({ debut: position(i.debut, ancre), fin: position(i.fin, ancre) })),
    }
  })
}
